import { Button } from '@mui/material'
import { FC, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { getContractPlan } from 'src/features/auth/getContractPlan'
import { useMutationAddTraceKey } from 'src/features/traceKeys/api/useMutationAddTraceKey'
import { useQueryTraceKeys } from 'src/features/traceKeys/api/useQueryTraceKeys'
import {
  getTraceKeyLimit,
  isTraceKeySupportedPlan,
} from 'src/features/traceKeys/utils/traceKeyLimit'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import {
  CreateTraceKeyForm,
  CreateTraceKeyFormData,
} from './components/CreateTraceKeyForm'

/**
 * 検索キー追加ページ
 */
const CreateTraceKeyPage: FC = () => {
  const history = useHistory()
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const addTraceKeyMutation = useMutationAddTraceKey()
  const { data: traceKeys = [] } = useQueryTraceKeys()

  const plan = getContractPlan()
  // このページ自体 TraceKeySupportedPlan 以外のプランでは表示されないはずだが念のため制御している
  if (!isTraceKeySupportedPlan(plan)) {
    throw new Error(`このページは${plan}プランでは利用できません`)
  }
  const limit = getTraceKeyLimit(plan)

  const form = useForm<CreateTraceKeyFormData>({
    defaultValues: {
      name: '',
      responseType: undefined,
    },
  })

  const handleBack = useCallback(() => {
    history.push('/traceKeys')
  }, [history])

  const onSubmit = useCallback(
    (data: CreateTraceKeyFormData) => {
      if (!data.responseType) {
        return
      }

      // 上限に達している場合は一覧にリダイレクトして追加処理を行わない
      if (traceKeys.length >= limit) {
        showErrorSnackbar('検索キーの数が上限に達したため、追加できません')
        history.push('/traceKeys')
        return
      }

      addTraceKeyMutation.mutate(
        {
          name: data.name,
          responseType: data.responseType,
        },
        {
          onSuccess: () => {
            showSuccessSnackbar(`「${data.name}」を追加しました`)
            history.push('/traceKeys')
          },
          onError: (error: unknown) => {
            if (isHTTPError(error)) {
              switch (error.status) {
                case 400:
                case 409:
                  showErrorSnackbar(error.errors.join('\n'))
                  break
                default:
                  showErrorSnackbar(`「${data.name}」の追加に失敗しました`)
                  break
              }
              return
            }
            showErrorSnackbar(`「${data.name}」の追加に失敗しました`)
          },
        },
      )
    },
    [
      addTraceKeyMutation,
      showSuccessSnackbar,
      showErrorSnackbar,
      history,
      traceKeys,
      limit,
    ],
  )

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Header
        sticky
        rightSlot={
          <Button
            type="submit"
            variant="contained"
            disabled={addTraceKeyMutation.isPending}
          >
            {addTraceKeyMutation.isPending ? '保存中...' : '保存'}
          </Button>
        }
      >
        <HeaderBackButtonMui
          onClick={handleBack}
          disabled={addTraceKeyMutation.isPending}
        />
        <HeaderTitle title="検索キーの追加" />
      </Header>

      <CreateTraceKeyForm form={form} />
    </form>
  )
}

export { CreateTraceKeyPage }
